// Setup's seat drawer over whatever page opened it (UX-A 1.7: the seat page's "Edit seat";
// MODALS: the chart card's Edit and the seat panel's "Assign a holder"), so editing a seat or its
// holders never leaves the chart. Closing it refreshes the chart the page reads. The Setup ›
// Seats URL (`?seat=`) still opens the same drawer there.
import { toast } from "@companyos/ui";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import type { Role } from "../../api/types";
import type { CompanyAccess } from "../company";
import { rolesQuery, seatsQuery } from "../queries";
import { SeatDrawer } from "../structure/seat-drawer";
import { useEveryPage } from "../use-every-page";
import { chartKeys } from "./chart-queries";

export function EditSeatDrawer({
  api,
  company,
  seatId,
  roles,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  /** The roles the caller already read; absent = read here. */
  roles?: Role[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // The seats the drawer's "Reports to" offers: every page, as Setup › Seats loads them.
  const seats = useInfiniteQuery(seatsQuery(api, company.id));
  useEveryPage(seats);
  const own = useInfiniteQuery({ ...rolesQuery(api, company.id), enabled: roles === undefined });
  return (
    <SeatDrawer
      api={api}
      company={company}
      seatId={seatId}
      seats={seats.data?.pages.flatMap((p) => p.data) ?? []}
      roles={roles ?? own.data?.pages.flatMap((p) => p.data) ?? []}
      // A save of the details or a holder assignment: the chart behind refreshes (the drawer's
      // writes invalidate it), a toast says so, and the drawer closes onto the page as it was.
      onSaved={(what) => {
        toast.add({ title: what === "holder" ? "Holder assigned" : "Seat saved", type: "success" });
        onClose();
      }}
      onClose={() => {
        void qc.invalidateQueries({ queryKey: chartKeys.orgChart(company.id) });
        onClose();
      }}
    />
  );
}
